import type { Appointment } from "@/features/clinic/types/appointment";
import type { ClinicInvoice } from "@/features/clinic/types/invoice";
import type { Contact } from "@/features/contact/types";

import { createId, pickRandom, randomBool, randomInt } from "./base";

import {
  countsTowardValue,
  sumInvoiceAmounts,
} from "@/features/clinic/helpers/invoice";
import { APPOINTMENT_STATUS } from "@/features/clinic/types/appointment";
import {
  INVOICE_REVIEW_STATE,
  INVOICE_SOURCE,
  INVOICE_STATUS,
} from "@/features/clinic/types/invoice";

const INVOICE_AMOUNTS = ["120.00", "250.00", "450.00", "800.00", "1200.00"];

const INVOICE_CODE_PREFIX = "INV-";

export const formatInvoiceCode = (sequence: number) =>
  `${INVOICE_CODE_PREFIX}${String(sequence).padStart(6, "0")}`;

/**
 * Continues the sequence from the highest code on record rather than the row
 * count, so a delete never frees a number for reuse.
 */
export const nextInvoiceCode = (invoices: ClinicInvoice[]) => {
  const highest = invoices.reduce((max, invoice) => {
    const sequence = Number(invoice.code.slice(INVOICE_CODE_PREFIX.length));

    return Number.isNaN(sequence) ? max : Math.max(max, sequence);
  }, 0);

  return formatInvoiceCode(highest + 1);
};

/**
 * Prices roughly half of the completed appointments so the invoice card has
 * data to show, while the rest stay unpriced to keep the "needs a value"
 * worklist populated. Writes the derived `value` back onto the appointment -
 * the same sum-of-non-void rule the backend applies.
 */
export function generateInvoices(
  appointments: Appointment[],
  contacts: Contact[],
): ClinicInvoice[] {
  const invoices: ClinicInvoice[] = [];
  const fallbackContactId = contacts[0]?.id ?? "mock-contact";

  appointments.forEach((appointment, index) => {
    if (appointment.status !== APPOINTMENT_STATUS.COMPLETED) return;
    if (index % 2 === 0) return;

    const count = randomInt(1, 2);
    const own: ClinicInvoice[] = [];

    for (let i = 0; i < count; i++) {
      own.push({
        id: createId(),
        appointmentId: appointment.id,
        contactId: appointment.contactId ?? fallbackContactId,
        code: formatInvoiceCode(invoices.length + own.length + 1),
        externalCode: randomBool(0.6) ? `INV-${randomInt(1000, 9999)}` : null,
        source: INVOICE_SOURCE.MANUAL,
        integrationId: null,
        invoiceDate: null,
        paymentType: null,
        createdByCrmUser: null,
        matchedUserId: null,
        subtotal: null,
        discount: null,
        vat: null,
        total: pickRandom(INVOICE_AMOUNTS),
        status: INVOICE_STATUS.PAID,
        reviewState: INVOICE_REVIEW_STATE.APPLIED,
        paidAt: appointment.endDate,
        notes: null,
        createdByUserId: null,
        organizationId: appointment.organizationId,
        workspaceId: appointment.workspaceId,
        createdAt: appointment.endDate,
        updatedAt: null,
      });
    }

    appointment.value = sumInvoiceAmounts(
      own.filter(countsTowardValue),
    ).toFixed(2);
    invoices.push(...own);
  });

  return invoices;
}
